import { Kind, print } from 'graphql';
import { type Exchange, mapExchange, type Operation } from 'urql';
import { pipe, tap } from 'wonka';

import { getChartItemIdsForRequest } from './chart-request-registry';
import {
  getDataInspectorState,
  isInspectorRecording,
} from './data-inspector.store';

const getOperationName = (operation: Operation) =>
  operation.query.definitions.find((def) => def.kind === 'OperationDefinition')
    ?.name?.value ?? 'unknown';

/**
 * The top-level fields the operation selects — `GetPayV3` → `['pay_v3']`.
 *
 * Read off the AST rather than a name→field lookup table, so a query added
 * tomorrow is labelled correctly without anyone remembering to register it.
 * `name.value` is deliberate: an aliased field reports its underlying name,
 * which is what the public API docs are anchored on.
 */
const getRootFields = (operation: Operation): string[] =>
  operation.query.definitions
    .filter((def) => def.kind === Kind.OPERATION_DEFINITION)
    .flatMap((def) => [...def.selectionSet.selections])
    .filter((selection) => selection.kind === Kind.FIELD)
    .map((selection) => selection.name.value);

/**
 * The path the request was fired from, read live rather than mirrored through
 * React state.
 *
 * It has to be read here, at dispatch. urql subscribes a query's source from
 * `useQuery`'s state initialiser — during the chart's render — and each
 * subscribe dispatches the operation synchronously, so a route pushed into the
 * store by an effect is always one navigation behind: the page you have just
 * opened issues every one of its queries before that effect runs, and they were
 * all filed under the page you left. `window.location` has no such lag — the
 * History API updates it before React re-renders at all.
 *
 * `pathname` only, matching `useLocation`: deeplink query strings would
 * otherwise split one page into a group per parameter set.
 */
const getCurrentRoute = () => window.location.pathname;

/**
 * urql's operation key repeats every time the same (query, variables) pair
 * re-runs, so it cannot identify one attempt. This maps the key to the record
 * currently in flight for it; latest attempt wins.
 */
const inFlightRecordIds = new Map<number, string>();

let recordCounter = 0;

/**
 * The `x-request-id` header `authExchange` appended. Read off the result's
 * operation rather than at dispatch: the auth exchange sits below this one,
 * so the header only exists on the operation that came back.
 */
const readRequestId = (operation: Operation): string | undefined => {
  const fetchOptions = operation.context.fetchOptions;
  const resolved =
    typeof fetchOptions === 'function' ? fetchOptions() : fetchOptions;
  const headers = resolved?.headers;
  if (headers === undefined) return undefined;
  if (headers instanceof Headers) {
    return headers.get('x-request-id') ?? undefined;
  }
  if (Array.isArray(headers)) {
    const pair = headers.find(
      ([name]) => name.toLowerCase() === 'x-request-id'
    );
    return pair?.[1];
  }
  const value = headers['x-request-id'];
  return typeof value === 'string' ? value : undefined;
};

/** Once per response, for admins only — recording is gated on the permission. */
const measureJsonLength = (data: unknown): number | undefined => {
  if (data === undefined) return undefined;
  try {
    return JSON.stringify(data)?.length;
  } catch {
    return undefined;
  }
};

const isRecordable = (operation: Operation) =>
  operation.kind === 'query' || operation.kind === 'mutation';

// urql types `OperationContext` with an index signature, so this reads back as
// `any` — narrow it at the boundary rather than trusting the shape.
const readStartTime = (operation: Operation): number | undefined => {
  const value: unknown = operation.context._startTime;
  return typeof value === 'number' ? value : undefined;
};

/**
 * Captures each operation and its result for the Data Inspector drawer.
 *
 * Sits directly after `cacheExchange` so it sees one entry per logical
 * operation rather than one per `useQuery` subscriber — the same placement, and
 * the same reason, as the Kibana network observer.
 *
 * Always installed, but inert unless the viewer is permitted the inspector:
 * the urql client is created above `UnleashFlags`, so access can only be
 * consulted per operation via the store rather than at client construction.
 *
 * Capture is gated on the permission, NOT on the user's toggle — a chart issues
 * its query once, so a recorder that only woke up when the panel was opened
 * could never show the requests behind the page already on screen.
 *
 * Note this can only see operations that were actually issued. A paused query
 * never reaches an exchange, so it cannot appear here.
 */
const recorder = mapExchange({
  onOperation(operation) {
    const state = getDataInspectorState();
    if (!isInspectorRecording(state) || !isRecordable(operation))
      return operation;

    // Anything no chart is subscribed to — taxonomy trees, saved sets, user
    // preferences — is plumbing, not the data behind a plot. Recording it
    // buries the handful of rows that answer "where did this chart's numbers
    // come from".
    const chartItemIds = getChartItemIdsForRequest(operation.key);
    if (chartItemIds.length === 0) return operation;

    // Now that this sits ABOVE `cacheExchange`, urql's dedup point is
    // downstream — so N cards sharing one (query, variables) dispatch N
    // operations through here with the same key, where previously the cache
    // collapsed them into one. Recording each would stack near-identical rows
    // whose only difference is how many cards had registered by that instant.
    // One record per in-flight key; it is released in `onResult`/`onError`, so
    // a genuine later re-run still opens a fresh one.
    if (inFlightRecordIds.has(operation.key)) return operation;

    recordCounter += 1;
    const id = `op-${recordCounter}`;
    inFlightRecordIds.set(operation.key, id);

    state.startRecord({
      id,
      operationKey: operation.key,
      operationName: getOperationName(operation),
      rootFields: getRootFields(operation),
      route: getCurrentRoute(),
      filterSignature: state.currentFilterSignature,
      filterParts: state.currentFilterParts,
      kind: operation.kind,
      variables: (operation.variables ?? {}) as Record<string, unknown>,
      query: print(operation.query),
      chartItemIds,
      dispatchedAt: performance.now(),
      fromCache: false,
      status: 'pending',
    });

    return operation;
  },

  onResult(result) {
    // No recording check here, deliberately. An in-flight id only exists
    // because `onOperation` opened a record, and permission can drop in
    // between — bailing out would strand the entry and block that key from
    // ever being recorded again. `finishRecord` no-ops if the record has since
    // been cleared, so finishing unconditionally is the safe direction.
    const id = inFlightRecordIds.get(result.operation.key);
    if (id === undefined) return result;

    // `_startTime` is stamped by `timingExchange`, which sits just before
    // `fetchExchange` — its absence means the result never left the client.
    const startTime = readStartTime(result.operation);
    const fromCache = startTime === undefined;
    const finishedAt = performance.now();

    getDataInspectorState().finishRecord(id, {
      status: result.error ? 'error' : 'success',
      error: result.error?.message,
      data: result.data,
      fromCache,
      fetchStartedAt: startTime,
      finishedAt,
      durationMs: startTime === undefined ? undefined : finishedAt - startTime,
      responseBytes: measureJsonLength(result.data),
      requestId: readRequestId(result.operation),
    });

    if (!result.stale) inFlightRecordIds.delete(result.operation.key);

    return result;
  },

  onError(error, operation) {
    const id = inFlightRecordIds.get(operation.key);
    if (id === undefined) return;

    getDataInspectorState().finishRecord(id, {
      status: 'error',
      error: error.message,
      finishedAt: performance.now(),
      requestId: readRequestId(operation),
    });
    inFlightRecordIds.delete(operation.key);
  },
});

/**
 * The last subscriber left before a result arrived, so the fetch is aborted.
 * Left alone the row would read "in flight" forever — and the key would stay
 * in the in-flight map, never to be recorded again this session.
 *
 * Watched on the operation stream itself: `mapExchange` passes teardowns
 * straight through without calling `onOperation`, so the recorder above never
 * sees them.
 */
const cancelOnTeardown = (operation: Operation) => {
  if (operation.kind !== 'teardown') return;
  const id = inFlightRecordIds.get(operation.key);
  if (id === undefined) return;
  inFlightRecordIds.delete(operation.key);
  getDataInspectorState().cancelRecord(id, performance.now());
};

export const dataInspectorExchange: Exchange = (input) => {
  const forward = recorder(input);
  return (ops$) => forward(pipe(ops$, tap(cancelOnTeardown)));
};
